// Resources
import { Questionnaire, Parameters, QuestionnaireResponse } from "fhir/r5";
// FHIR
import Client from "fhir-kit-client";

/////////////////////////////////////
//             Client              //
/////////////////////////////////////

const fhirClient = new Client({
  baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
});

const fhirOperationClient = new Client({
  baseUrl: process.env.REACT_APP_QUESTIONNAIRE_URL ?? "fhir",
});

/**
 * Load Questionnaire from the back to populate the fields.
 *
 * @returns the promise of a Questionnaire.
 */
async function loadQuestionnaire(questionnaireId: string): Promise<Questionnaire> {
  return fhirClient.read({
    resourceType: "Questionnaire",
    id: questionnaireId ?? "",
  }) as Promise<Questionnaire>;
}

/**
 * Create Questionnaire in the FHIR server.
 *
 * @returns the promise of a Questionnaire.
 */
async function createQuestionnaire(questionnaire: Questionnaire): Promise<Questionnaire> {
  return fhirClient.create({
    resourceType: "Questionnaire",
    body: questionnaire,
  }) as Promise<Questionnaire>;
}

/**
 * Function to use the operation Populate for a questionnaire
 */
async function populate(questionnaire: Questionnaire, subjectID: string): Promise<QuestionnaireResponse> {
  // Use the parameter questionnaire
  let parameter: Parameters = {
    resourceType: "Parameters",
    parameter: [
      {
        name: "questionnaire",
        resource: questionnaire,
      },
      {
        name: "subject",
        valueString: subjectID,
      },
    ],
  };
  return fhirOperationClient.operation({
    name: "populate",
    input: parameter,
    resourceType: "Questionnaire",
  });
}

async function evaluateCqlExpression(
  expression: string,
  questionnaireResponse: QuestionnaireResponse,
  variables: Map<string, any>,
): Promise<any> {
  const parameters: Parameters = {
    resourceType: "Parameters",
    parameter: [
      {
        name: "expression",
        valueString: expression,
      },
      {
        name: "questionnaire-response",
        resource: questionnaireResponse,
      },
      {
        name: "variables",
        resource: {
          resourceType: "Parameters",
          parameter: Array.from(variables.entries()).map(([name, value]) => ({
            name,
            valueString: value !== undefined && value !== null ? String(value) : "",
          })),
        },
      },
    ],
  };

  return fhirOperationClient.operation({
    name: "evaluate",
    input: parameters,
    resourceType: "Library",
  });
}
function extractCqlValue(result: any): any {
  const parameters = result?.parameter;

  if (!Array.isArray(parameters)) {
    return null;
  }

  const firstValueParam = parameters.find(
    (param: any) =>
      param.valueDecimal !== undefined ||
      param.valueInteger !== undefined ||
      param.valueString !== undefined ||
      param.valueBoolean !== undefined,
  );

  if (!firstValueParam) {
    return null;
  }

  return (
    firstValueParam.valueDecimal ??
    firstValueParam.valueInteger ??
    firstValueParam.valueString ??
    firstValueParam.valueBoolean ??
    null
  );
}

const cqlEvaluator = async (
  expression: string,
  questionnaireResponse: QuestionnaireResponse,
  variables: Map<string, any>,
): Promise<any> => {
  const result = await evaluateCqlExpression(expression, questionnaireResponse, variables);

  return extractCqlValue(result);
};
///////////////////////////////
//        exports            //
///////////////////////////////

const QuestionnaireService = {
  populate,
  createQuestionnaire,
  loadQuestionnaire,
  evaluateCqlExpression,
  extractCqlValue,
  cqlEvaluator,
};

export default QuestionnaireService;
